import platform_icon_openRTB from 'assets/openRTB.png';
import channel_icon_rtb from 'assets/channel_rtb.png';
import {
  Performance,
  ReportSection,
  NoteSection,
  Platform,
  Group,
  Column,
  TargetKPI,
  Note
} from './FinalReport';
import { OrderType } from 'core/order/Order';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export const ICON_MAP: {
  [key: string]: {
    src: string;
    size: 'ordinary' | 'small';
  };
} = Object.freeze({
  [Platform.OPEN_RTB]: {
    src: platform_icon_openRTB,
    size: 'ordinary'
  },
  [ChannelGroupSetting.RTB.key]: {
    src: channel_icon_rtb,
    size: 'small'
  }
});

export const KPI_COLUMN_MAP: {
  [key: string]: Column;
} = Object.freeze({
  [TargetKPI.IMPRESSION]: Column.IMPRESSIONS,
  [TargetKPI.CLICK]: Column.CLICKS,
  [TargetKPI.CONVERSION]: Column.CLICKS,
  [TargetKPI.VIEW]: Column.VIEWS
});

/**
 * Default group ordering for the L1 object deep-dive section. Shared by every
 * static channel (RTB) and reused as the fallback in
 * `TenmaxReportContent` when the report's L1 object dimension is a dynamic
 * channel (e.g. "PIC Outdoor", "WI Fridge") not keyed in this template.
 */
export const DEFAULT_L1_DEEP_DIVE_GROUPS = [
  Group.AGE_GROUP,
  Group.GENDER,
  Group.CREATIVE,
  Group.TENMAX_SEGMENT,
  Group.TRAFFIC_CATEGORY,
  Group.SERVICE_AREA
];

export const REPORT_GROUPS_TEMPLATE = Object.freeze({
  [Performance.OVERALL]: {
    [ReportSection.PLATFORM]: [Group.PLATFORM],
    [ReportSection.L1OBJECT]: [Group.L1OBJECT]
  },
  [Performance.DEEP_DIVE]: {
    [ReportSection.PLATFORM]: {
      [Platform.OPEN_RTB]: [
        Group.AGE_GROUP,
        Group.GENDER,
        Group.CREATIVE,
        Group.AUDIENCE_SEGMENT,
        Group.DOMAIN,
        Group.SERVICE_AREA
      ]
    },
    [ReportSection.L1OBJECT]: {
      [ChannelGroupSetting.RTB.key]: DEFAULT_L1_DEEP_DIVE_GROUPS
    },
    [ReportSection.ORDER]: [
      Group.AGE_GROUP,
      Group.GENDER,
      Group.CREATIVE,
      Group.TENMAX_SEGMENT,
      Group.TRAFFIC_CATEGORY,
      Group.SERVICE_AREA
    ],
    [ReportSection.USERS]: [
      Group.AGE_GROUP,
      Group.GENDER,
      Group.AUDIENCE_SEGMENT,
      Group.REGION
    ]
  }
});

export const COLUMNS_TEMPLATE = Object.freeze({
  [Performance.OVERALL]: {
    [ReportSection.PLATFORM]: [
      Column.IMPRESSIONS,
      Column.REACH,
      Column.CLICKS,
      Column.CTR,
      Column.CPC,
      Column.CPM
    ],
    [ReportSection.L1OBJECT]: [
      Column.IMPRESSIONS,
      Column.REACH,
      Column.CLICKS,
      Column.CONVS,
      Column.VIEWS,
      Column.CTR,
      Column.VTR,
      Column.CPM,
      Column.CPC,
      Column.CPA,
      Column.CPV,
      Column.SPEND
    ]
  },
  [Performance.DEEP_DIVE]: {
    [ReportSection.PLATFORM]: [
      Column.IMPRESSIONS,
      Column.REACH,
      Column.CLICKS,
      Column.CTR,
      Column.CPC,
      Column.CPM
    ],
    [ReportSection.USERS]: [
      Column.SALES,
      Column.TRANSACTIONS,
      Column.BASKET_SIZE,
      Column.ITEMS_SOLD,
      Column.NEW_USERS,
      Column.LAPSE_USERS,
      Column.EXISTING_USERS
    ],
    [ReportSection.L1OBJECT]: [
      Column.IMPRESSIONS,
      Column.REACH,
      Column.CLICKS,
      Column.VIEWS,
      Column.CTR,
      Column.VTR,
      Column.CPM,
      Column.CPC,
      Column.CPV
    ],
    [ReportSection.ORDER]: [
      Column.IMPRESSIONS,
      Column.REACH,
      Column.CLICKS,
      Column.VIEWS,
      Column.CTR,
      Column.VTR,
      Column.CPM,
      Column.CPC,
      Column.CPV
    ]
  }
});

export const NOTES_TEMPLATE = Object.freeze({
  [Performance.OVERALL]: {
    [NoteSection.PLATFORM]: [
      Note.ACTUAL_IMPRESSIONS,
      Note.TARGET_IMPRESSIONS,
      Note.REACH,
      Note.CLICKS,
      Note.CTR,
      Note.CPC,
      Note.CPM,
      Note.BENCHMARKS
    ],
    [NoteSection.ADS_IMPACT]: [
      Note.SALES,
      Note.TRANSACTIONS,
      Note.ITEMS_SOLD,
      Note.NEW_USERS,
      Note.LAPSE_USERS,
      Note.EXISTING_USERS,
      Note.BASKET_SIZE
    ],
    [NoteSection.L1OBJECT]: [
      Note.ACTUAL_IMPRESSIONS,
      Note.TARGET_IMPRESSIONS,
      Note.REACH,
      Note.CLICKS,
      Note.VIEWS,
      Note.CTR,
      Note.VTR,
      Note.CPM,
      Note.CPC,
      Note.CPV
    ]
  },
  [Performance.DEEP_DIVE]: {
    [NoteSection.ADS_IMPACT_USERS]: [
      Note.NEW_USERS,
      Note.LAPSE_USERS,
      Note.EXISTING_USERS
    ],
    [NoteSection.USERS]: [
      Note.SALES,
      Note.TRANSACTIONS,
      Note.ITEMS_SOLD,
      Note.NEW_USERS,
      Note.LAPSE_USERS,
      Note.EXISTING_USERS,
      Note.BASKET_SIZE,
      Note.OTHERS
    ],
    [NoteSection.PLATFORM]: {
      [Platform.OPEN_RTB]: [
        Note.IMPRESSIONS,
        Note.REACH,
        Note.CLICKS,
        Note.CTR,
        Note.CPC,
        Note.CPM,
        Note.SERVICE_AREA,
        Note.BENCHMARKS,
        Note.OTHERS
      ]
    },
    [NoteSection.L1OBJECT]: [
      Note.IMPRESSIONS,
      Note.REACH,
      Note.CLICKS,
      Note.VIEWS,
      Note.CTR,
      Note.VTR,
      Note.CPM,
      Note.CPC,
      Note.CPV,
      Note.OTHERS
    ]
  }
});

export const L1_OBJECT_PRIORITY: string[] = [ChannelGroupSetting.RTB.key];

export const ORDER_OVERALL_PRIORITY: {
  [key: string]: string[];
} = {
  [OrderType.TENMAX]: [
    Column.IMPRESSIONS,
    Column.CLICKS,
    Column.CONVS,
    Column.VIEWS,
    Column.CTR,
    Column.VTR,
    Column.CPM,
    Column.CPC,
    Column.CPA,
    Column.CPV,
    Column.SPEND
  ]
};

export const ORDER_COLUMN_PRIORITY: {
  [key: string]: string[];
} = {
  [OrderType.TENMAX]: [
    Column.IMPRESSIONS,
    Column.REACH,
    Column.CLICKS,
    Column.CONVS,
    Column.VIEWS,
    Column.CTR,
    Column.VTR,
    Column.CPM,
    Column.CPC,
    Column.CPA,
    Column.CPV,
    Column.SPEND
  ]
};

export const GROUP_LIMIT_MAP: {
  [key: string]: number;
} = {
  [Group.CREATIVE]: 5,
  [Group.TENMAX_SEGMENT]: 6,
  [Group.TRAFFIC_CATEGORY]: 5,
  [Group.SERVICE_AREA]: 5
};

export const SUMMABLE_COLUMNS: string[] = [
  Column.IMPRESSIONS,
  Column.RAW_IMPRESSIONS,
  Column.REACH,
  Column.CLICKS,
  Column.VIEWS,
  Column.SALES,
  Column.TRANSACTIONS,
  Column.ITEMS_SOLD,
  Column.NEW_USERS,
  Column.LAPSE_USERS,
  Column.EXISTING_USERS,
  Column.BASKET_SIZE,
  Column.CONVS,
  Column.SPEND
];

export const ORDER_CHART_COLORS: {
  [key: string]: {
    [key: number]: string;
  };
} = {
  [OrderType.TENMAX]: {
    0: '#007f90',
    1: '#d1eff1'
  }
};
